import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function CarpentryPage() {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [callbackData, setCallbackData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: 'morning',
    message: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: 'residential',
    teamSize: '1-2'
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);

    const timer = setTimeout(() => {
      if (!formSubmitted) {
        setShowLeadForm(true);
      }
    }, 8000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [formSubmitted]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('businessType', formData.businessType);
      formBody.append('teamSize', formData.teamSize);

      await fetch('https://readdy.ai/api/form/d69b95vmvg9ih2c79uj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setFormSubmitted(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', callbackData.name);
      formBody.append('phone', callbackData.phone);
      formBody.append('email', callbackData.email);
      formBody.append('preferredTime', callbackData.preferredTime);
      formBody.append('message', callbackData.message);

      await fetch('https://readdy.ai/api/form/d69b95vmvg9ih2c79ujg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setCallbackSubmitted(true);
      setShowCallbackForm(false);
      setTimeout(() => setCallbackSubmitted(false), 5000);
    } catch (error) {
      console.error('Callback form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: 'ri-home-4-line',
      title: 'Custom Cabinetry',
      description: 'Generate detailed quotes for kitchen cabinets, wardrobes, bathroom vanities, and custom storage solutions with material breakdowns.',
      price: 'From $2,500'
    },
    {
      icon: 'ri-door-line',
      title: 'Door & Window Installation',
      description: 'Quote door hanging, window frames, architraves, and hardware installation with accurate labour and material costs.',
      price: 'From $350'
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Decking & Pergolas',
      description: 'Create comprehensive quotes for timber decks, pergolas, gazebos, and outdoor structures with council compliance notes.',
      price: 'From $4,800'
    },
    {
      icon: 'ri-stairs-line',
      title: 'Staircase Construction',
      description: 'Generate quotes for timber staircases, balustrades, handrails, and custom stair designs with 3D visualization.',
      price: 'From $3,200'
    },
    {
      icon: 'ri-tools-line',
      title: 'Structural Framing',
      description: 'Quote wall framing, roof trusses, floor joists, and structural timber work with engineering specifications.',
      price: 'From $8,500'
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Finishing & Trim Work',
      description: 'Create quotes for skirting boards, cornices, picture rails, and decorative mouldings with precision measurements.',
      price: 'From $180'
    }
  ];

  const stats = [
    { value: '15+', label: 'Hours Saved Weekly', icon: 'ri-time-line' },
    { value: '3,200+', label: 'Carpenters Using It', icon: 'ri-team-line' },
    { value: '88%', label: 'Quote Win Rate', icon: 'ri-trophy-line' },
    { value: '$12K', label: 'Avg Monthly Revenue Increase', icon: 'ri-money-dollar-circle-line' }
  ];

  const testimonials = [
    {
      name: 'David Mitchell',
      business: 'Mitchell Carpentry & Joinery',
      specialty: 'Custom Cabinetry',
      image: 'https://public.readdy.ai/ai/img_res/acb2fc320c738853345a05a91ba7292e.jpg',
      quote: 'The cabinet quoting feature has transformed my business. I can now provide detailed breakdowns for custom kitchens in 15 minutes instead of 2 hours. My clients love the professional presentation.',
      results: 'Increased monthly revenue by $14,000'
    },
    {
      name: 'Sarah Thompson',
      business: 'Thompson Timber Works',
      specialty: 'Decking & Outdoor',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20carpenter%20with%20premium%20timber%20decking%20materials%20and%20advanced%20power%20tools%20wearing%20quality%20work%20clothes%20confident%20pose%20in%20luxury%20outdoor%20construction%20setting%20with%20beautiful%20hardwood%20deck%20materials%20natural%20daylight%20cinematic%20photography&width=400&height=400&seq=carp-test-premium-2&orientation=squarish',
      quote: 'Quoting decks used to take forever with all the timber calculations. Now the system does it automatically and even suggests the best timber options for the budget. Game changer!',
      results: 'Won 18 deck projects in 2 months'
    },
    {
      name: 'Michael OConnor',
      business: 'OConnor Building Services',
      specialty: 'Residential Framing',
      image: 'https://readdy.ai/api/search-image?query=experienced%20carpenter%20on%20premium%20residential%20construction%20site%20with%20structural%20framing%20timber%20and%20professional%20tools%20wearing%20hard%20hat%20confident%20appearance%20in%20clean%20modern%20building%20with%20gleaming%20materials%20natural%20lighting%20ultra%20sharp%20cinematic%20quality&width=400&height=400&seq=carp-test-premium-3&orientation=squarish',
      quote: 'Managing multiple framing jobs was chaos before. Now I can quote entire house frames accurately, track materials from suppliers, and keep builders updated automatically.',
      results: 'Secured 4 major builder contracts'
    }
  ];

  const supplierPricing = [
    {
      category: 'Structural Timber',
      items: ['Pine framing', 'Hardwood beams', 'LVL & engineered', 'Treated pine'],
      icon: 'ri-stack-line'
    },
    {
      category: 'Sheet Materials',
      items: ['Plywood', 'MDF & particle board', 'Hardboard', 'Cement sheet'],
      icon: 'ri-layout-4-line'
    },
    {
      category: 'Finishing Timber',
      items: ['Skirting & architraves', 'Mouldings', 'Decking boards', 'Cladding'],
      icon: 'ri-brush-line'
    },
    {
      category: 'Hardware & Fixings',
      items: ['Screws & nails', 'Brackets & connectors', 'Hinges & handles', 'Adhesives'],
      icon: 'ri-settings-3-line'
    }
  ];

  const caseStudies = [
    {
      title: 'Custom Kitchen Business Growth',
      carpenter: 'Premium Kitchens Melbourne',
      challenge: 'Struggled to provide quick, accurate quotes for custom kitchens. Lost jobs to competitors with faster turnaround times.',
      solution: 'Implemented automated kitchen quoting with real-time timber and hardware pricing, plus 3D visualization integration.',
      results: [
        'Quote generation time reduced from 3 hours to 20 minutes',
        'Kitchen job bookings increased by 280%',
        'Average job value increased from $18,000 to $24,500',
        'Customer satisfaction score improved to 4.8/5'
      ],
      image: 'https://readdy.ai/api/search-image?query=stunning%20luxury%20custom%20kitchen%20with%20premium%20timber%20cabinetry%20being%20installed%20by%20professional%20carpenter%20gleaming%20hardwood%20finishes%20marble%20countertops%20bright%20clean%20residential%20interior%20showing%20quality%20craftsmanship%20attention%20to%20detail%20and%20sophisticated%20design%20cinematic%20lighting&width=800&height=500&seq=carp-case-premium-1&orientation=landscape'
    },
    {
      title: 'Deck Building Contractor Efficiency',
      carpenter: 'Aussie Deck Builders',
      challenge: 'Managing quotes for multiple deck projects was time-consuming. Timber calculations and waste factors led to pricing errors.',
      solution: 'Adopted comprehensive quoting system with automatic timber calculations and supplier integration.',
      results: [
        'Won 12 major deck contracts worth $180K total',
        'Material cost accuracy improved by 92%',
        'Project quoting time reduced by 70%',
        'Profit margins increased by 15%'
      ],
      image: 'https://readdy.ai/api/search-image?query=beautiful%20premium%20timber%20deck%20construction%20in%20progress%20with%20professional%20carpenter%20working%20on%20luxury%20outdoor%20entertaining%20area%20gleaming%20hardwood%20decking%20modern%20australian%20home%20backyard%20bright%20sunny%20day%20quality%20materials%20and%20expert%20installation%20cinematic%20photography&width=800&height=500&seq=carp-case-premium-2&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Floating Callback Button */}
      <button
        onClick={() => setShowCallbackForm(true)}
        className="fixed left-6 bottom-6 z-40 flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full shadow-2xl hover:shadow-amber-500/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
      >
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
          <i className="ri-phone-line text-xl"></i>
        </div>
        <div className="pr-2">
          <div className="font-semibold text-sm whitespace-nowrap">Request Callback</div>
          <div className="text-amber-100 text-xs whitespace-nowrap">We'll call you back</div>
        </div>
      </button>

      {/* Callback Request Modal */}
      {showCallbackForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowCallbackForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <div className="bg-gradient-to-br from-amber-600 to-orange-600 p-8 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-phone-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">Request a Callback</h3>
              <p className="text-amber-100">Leave your details and we'll call you back within 2 hours during business hours.</p>
            </div>

            <form
              id="carpentry-callback-form"
              data-readdy-form
              onSubmit={handleCallbackSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={callbackData.name}
                  onChange={(e) => setCallbackData({ ...callbackData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={callbackData.phone}
                  onChange={(e) => setCallbackData({ ...callbackData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  placeholder="0412 345 678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={callbackData.email}
                  onChange={(e) => setCallbackData({ ...callbackData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  placeholder="john@email.com (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Call Time</label>
                <select
                  name="preferredTime"
                  value={callbackData.preferredTime}
                  onChange={(e) => setCallbackData({ ...callbackData, preferredTime: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="morning">Morning (9am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 7pm)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What can we help with?</label>
                <textarea
                  name="message"
                  value={callbackData.message}
                  onChange={(e) => setCallbackData({ ...callbackData, message: e.target.value.slice(0, 500) })}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm resize-none"
                  placeholder="Tell us briefly what you'd like to discuss..."
                />
                <div className="text-xs text-gray-400 text-right mt-1">{callbackData.message.length}/500</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-semibold hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-phone-line"></i>
                    Request Callback
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Callback Success Toast */}
      {callbackSubmitted && (
        <div className="fixed bottom-6 left-6 z-50 bg-amber-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Callback Requested!</div>
            <div className="text-amber-100 text-sm">We'll call you back soon</div>
          </div>
        </div>
      )}

      {/* Floating Lead Capture Form */}
      {showLeadForm && !formSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <div className="bg-gradient-to-br from-amber-600 to-orange-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-gift-line"></i>
                Limited Time Offer
              </div>
              <h3 className="text-2xl font-bold mb-2">Get Your Free Carpentry Business Audit</h3>
              <p className="text-amber-100">Plus 30-day extended trial when you book a demo today</p>
            </div>

            <form
              id="carpentry-lead-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  placeholder="john@carpentry.com.au"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  placeholder="0412 345 678"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="cabinetry">Cabinetry</option>
                    <option value="framing">Framing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Size</label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="1-2">Just me / 1-2</option>
                    <option value="3-5">3-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="10+">10+ people</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-semibold hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Get My Free Audit + Demo
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sticky Bottom CTA Bar */}
      {showStickyBar && !formSubmitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-amber-600 to-orange-600 shadow-2xl shadow-black/20 transform transition-transform duration-300">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full">
                <i className="ri-hammer-fill text-white"></i>
                <span className="text-white text-sm font-medium">Carpenters Only</span>
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">Get 30% Off Your First 3 Months</div>
                <div className="text-amber-100 text-sm hidden sm:block">Plus free business audit worth $499</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLeadForm(true)}
                className="px-6 py-3 bg-white text-amber-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-2"></i>
                Claim Offer
              </button>
              <button
                onClick={() => setShowStickyBar(false)}
                className="p-2 text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {formSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Thanks! We'll be in touch soon</div>
            <div className="text-emerald-100 text-sm">Check your email for confirmation</div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <i className="ri-hammer-fill text-amber-600"></i>
                <span className="text-sm font-medium text-amber-900">For Professional Carpenters</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Carpentry Quoting<br />
                <span className="text-amber-600">Made Simple</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Generate professional carpentry quotes in minutes. From custom cabinetry to timber framing,
                streamline your quoting process and win more jobs with accurate, detailed estimates.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/demo"
                  className="px-8 py-4 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Book Free Demo
                </Link>
                <Link
                  to="/invoice-demo"
                  className="px-8 py-4 bg-white text-amber-600 rounded-lg font-semibold border-2 border-amber-600 hover:bg-amber-50 transition-all duration-300 whitespace-nowrap"
                >
                  Try Quote Generator
                </Link>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-600"></i>
                  <span><strong className="text-gray-900">3.5 min</strong> avg quote time</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-amber-600"></i>
                  <span>Australian Standards</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-3xl transform rotate-3"></div>
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20custom%20timber%20cabinetry%20workshop%20with%20professional%20australian%20carpenter%20crafting%20premium%20kitchen%20cabinets%20using%20advanced%20woodworking%20machinery%20gleaming%20hardwood%20materials%20and%20precision%20tools%20in%20modern%20bright%20workshop%20with%20natural%20lighting%20showing%20expert%20craftsmanship%20and%20attention%20to%20detail%20cinematic%20quality&width=600&height=700&seq=carp-hero-premium-1&orientation=portrait"
                alt="Professional Carpenter"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">$24,800</div>
                    <div className="text-sm text-gray-600">Kitchen quote sent in 12 min</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Carpentry Trade Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to quote carpentry jobs quickly and accurately
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-amber-600 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-600 transition-colors duration-300">
                  <i className={`${feature.icon} text-3xl text-amber-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                <div className="text-amber-600 font-semibold">{feature.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supplier Pricing Integration */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Real-Time Timber & Hardware Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrated pricing from major Australian timber and hardware suppliers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supplierPricing.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-amber-100 shadow-lg"
              >
                <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${category.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-amber-600 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">Integrated with leading suppliers:</p>
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="text-2xl font-bold text-gray-400">Bunnings</div>
              <div className="text-2xl font-bold text-gray-400">Bowens</div>
              <div className="text-2xl font-bold text-gray-400">Dahlsens</div>
              <div className="text-2xl font-bold text-gray-400">Mitre 10</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Carpenters Across Australia
            </h2>
            <p className="text-xl text-gray-600">
              See how carpenters are growing their businesses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                    <div className="text-xs text-amber-600 font-medium">{testimonial.specialty}</div>
                  </div>
                </div>

                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400"></i>
                  ))}
                </div>

                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.quote}"</p>

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                    <i className="ri-arrow-up-line"></i>
                    {testimonial.results}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Carpentry Business Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real results from carpenters who transformed their businesses
            </p>
          </div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-amber-50 rounded-3xl overflow-hidden shadow-xl"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="p-12">
                    <div className="inline-block px-4 py-2 bg-amber-600 text-white rounded-full text-sm font-semibold mb-6">
                      Case Study
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{study.title}</h3>
                    <div className="text-amber-600 font-semibold mb-6">{study.carpenter}</div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-error-warning-line text-red-500"></i>
                          Challenge
                        </h4>
                        <p className="text-gray-700">{study.challenge}</p>
                      </div>

                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-lightbulb-line text-amber-600"></i>
                          Solution
                        </h4>
                        <p className="text-gray-700">{study.solution}</p>
                      </div>

                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-trophy-line text-green-600"></i>
                          Results
                        </h4>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <i className="ri-check-line text-green-600 mt-1"></i>
                              <span>{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="relative h-full min-h-[400px]">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-600 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Carpentry Business?
          </h2>
          <p className="text-xl text-amber-100 mb-8">
            Join 3,200+ carpenters who are quoting faster, winning more jobs, and growing their revenue
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link
              to="/demo"
              className="px-8 py-4 bg-white text-amber-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book Your Free Demo
            </Link>
            <Link
              to="/invoice-demo"
              className="px-8 py-4 bg-amber-700 text-white rounded-lg font-semibold hover:bg-amber-800 transition-all duration-300 border-2 border-white whitespace-nowrap"
            >
              Try Quote Generator
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-amber-100">
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
